/**
 * Wire schemas shared by server and web. Shapes track the Firestore-era
 * schemas (old plus.schema.ts) closely so the web port is mechanical —
 * differences are deliberate and noted inline.
 */
import { z } from "zod";

export const AppUserSchema = z.object({
  id: z.number().int(),
  msOid: z.string(),
  email: z.string().email(),
  displayName: z.string().nullable(),
});
export type AppUser = z.infer<typeof AppUserSchema>;

// --- models ------------------------------------------------------------

export const ModelSchema = z.object({
  model: z.string().min(1),
  series: z.string().min(1),
  description: z.string(),
  photo_url: z.string(),
  photo_match: z.enum(["exact", "series_fallback", "none", "user_added"]),
  product_page: z.string(),
  category: z.string().optional(),
  photo_url_fullres_unverified: z.string().optional(),
});
export type Model = z.infer<typeof ModelSchema>;

// --- model templates ----------------------------------------------------

export const ModelTemplateFieldSchema = z.object({
  position: z.union([z.number().int(), z.string()]),
  name: z.string(),
  codes: z.record(z.string(), z.string()).optional(),
  values: z.string().optional(),
  note: z.string().optional(),
  meaning_uncertain: z.union([z.boolean(), z.string()]).optional(),
});
export type ModelTemplateField = z.infer<typeof ModelTemplateFieldSchema>;

export const ModelTemplateAccessorySchema = z.object({
  code: z.string(),
  meaning: z.string(),
  category: z.string(),
});
export type ModelTemplateAccessory = z.infer<typeof ModelTemplateAccessorySchema>;

export const ModelTemplateSchema = z.object({
  series_id: z.string().min(1),
  heading: z.string(),
  example: z.string(),
  catalog_page: z.string().optional(),
  fields: z.array(ModelTemplateFieldSchema),
  accessories: z.array(ModelTemplateAccessorySchema).optional(),
  notes: z.array(z.string()).optional(),
});
export type ModelTemplate = z.infer<typeof ModelTemplateSchema>;

// --- accessory photos ----------------------------------------------------

export const MAX_ACCESSORY_PHOTOS_PER_CODE = 20;
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

// image_url is now a short-lived signed read URL minted per response (the
// media bucket is private, unlike Firebase Storage's tokened public URLs).
// image_path remains the stable per-upload identity.
export const AccessoryPhotoSchema = z.object({
  image_url: z.string(),
  image_path: z.string(),
  uploaded_by: z.string(), // uploader email, for display + gallery filter
  uploaded_at: z.string(), // ISO
  caption: z.string().optional(),
});
export type AccessoryPhoto = z.infer<typeof AccessoryPhotoSchema>;

export const UploadUrlRequestSchema = z.object({
  code: z.string().min(1),
  fileName: z.string().min(1),
  contentType: z.string().regex(/^image\/(?!heic|heif)/, "Only non-HEIC images are allowed."),
});
export type UploadUrlRequest = z.infer<typeof UploadUrlRequestSchema>;

export const UploadUrlResponseSchema = z.object({
  uploadUrl: z.string(),
  imagePath: z.string(),
  // Client must send these headers on the PUT — the signature enforces them
  // (including the 5 MB content-length-range cap).
  headers: z.record(z.string(), z.string()),
});
export type UploadUrlResponse = z.infer<typeof UploadUrlResponseSchema>;

// --- discrepancy reports --------------------------------------------------

export const DiscrepancyReportStatusSchema = z.enum(["open", "triaged", "resolved"]);
export type DiscrepancyReportStatus = z.infer<typeof DiscrepancyReportStatusSchema>;

export const DiscrepancyReportSchema = z.object({
  id: z.number().int(),
  reporter: z.string(), // email, for display
  status: DiscrepancyReportStatusSchema,
  body: z.string(),
  context: z.object({ code: z.string().optional(), url: z.string().optional() }).optional(),
  created_at: z.string(),
});
export type DiscrepancyReport = z.infer<typeof DiscrepancyReportSchema>;

export const SubmitReportSchema = z.object({
  body: z.string().min(1),
  context: z.object({ code: z.string().optional(), url: z.string().optional() }).optional(),
});
export type SubmitReport = z.infer<typeof SubmitReportSchema>;

// --- model notes -----------------------------------------------------------

export const ModelNoteSchema = z.object({
  id: z.number().int(), // new: Postgres id (Firestore notes had no client-visible id)
  modelCode: z.string(),
  text: z.string(),
  author: z.string(), // email, for display + "own note" UI affordances
  created_at: z.string(),
});
export type ModelNote = z.infer<typeof ModelNoteSchema>;

export const AddNoteSchema = z.object({
  modelCode: z.string().min(1),
  text: z.string().min(1),
});
export type AddNote = z.infer<typeof AddNoteSchema>;
